import {FloatType, HalfFloatType, RGBAFormat, Texture, WebGLRenderer} from "three";
import {TextureHelper} from "features/apartmentViewer/ApartmentViewer/helpers/TextureHelper";
import {ApartmentViewerService} from "features/apartmentViewer/services/ApartmentViewerService/service";
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";
import {ApartmentViewerSettings} from "features/apartmentViewer/ApartmentViewer/ApartmentViewerSettings";

import init, {
    process_panorama_texture
} from 'features/apartmentViewer/ApartmentViewer/wasm/jm-image-processor/jm_image_processor';


export class HDRTextureProcessor {
    width: number = 0;
    height: number = 0;
    pixelData: Float32Array = new Float32Array();
    floatView: Float32Array = new Float32Array();
    int32View: Int32Array = new Int32Array();

    private textureCache = new Map<string, Texture>();

    constructor(
        private textureHelper: TextureHelper,
        private siteData: SiteModel,
        private settings: ApartmentViewerSettings,
        private renderer: WebGLRenderer
    ) {}

    initPixelData(width: number, height: number): void {
        // Initialize width, height, and pixel data arrays
        this.width = width;
        this.height = height;
        this.pixelData = new Float32Array(width * height * 4);
        this.floatView = new Float32Array(1);
        this.int32View = new Int32Array(this.floatView.buffer);
    }

    // Create a 32-bit texture from pixel data
    createDesktopTexture(): Texture {
        return this.textureHelper.createDataTexture(this.pixelData, this.width, this.height, RGBAFormat, FloatType);
    }

    createMobileTexture(): Texture {
        const length = this.pixelData.length;
        const halfFloatData = new Uint16Array(length);
        for (let i = 0; i < length; i++) {
            halfFloatData[i] = this.convertTo16Bit(this.pixelData[i]);
        }
        return this.textureHelper.createDataTexture(halfFloatData, this.width, this.height, RGBAFormat, HalfFloatType);
    }


    // Convert a 32-bit float to a 16-bit half-float representation
    convertTo16Bit(value: number): number {
        this.floatView[0] = value;
        const intValue = this.int32View[0];
        let sign = (intValue >> 16) & 0x8000;
        let mantissa = (intValue >> 12) & 0x07ff;
        const exponent = (intValue >> 23) & 0xff;

        if (exponent < 103) {
            return sign; // Underflow to zero
        } else if (exponent > 142) {
            sign |= 0x7c00; // Overflow to infinity
            sign |= (exponent === 255 ? 0 : 1) && (intValue & 0x7fffff); // NaN handling
            return sign;
        } else if (exponent < 113) {
            mantissa |= 0x0800;
            sign |= (mantissa >> (114 - exponent)) + ((mantissa >> (113 - exponent)) & 1);
            return sign; // Denormalized value
        } else {
            sign |= ((exponent - 112) << 10) | (mantissa >> 1);
            sign += mantissa & 1; // Round to nearest even
            return sign;
        }
    }

    async loadPanoramaTexture(markerId: string): Promise<Texture> {

        if (this.textureCache.has(`${this.siteData.currentFurnishing}-${markerId}-${this.settings.isMobile}`)) {
            return this.textureCache.get(`${this.siteData.currentFurnishing}-${markerId}-${this.settings.isMobile}`)!;
        }

        if(!this.siteData.currentApartment) return;

        const arrayBuffer = await ApartmentViewerService.getQHDRFile(this.siteData.stageId, this.siteData.currentApartment.id, this.siteData.currentFurnishing, markerId, this.settings.isMobile);

        const array = new Uint8Array(arrayBuffer);

        await init();

        const result = process_panorama_texture(array);


        this.initPixelData(result.width, result.height);

        this.pixelData = result.get_pixel_data();

        let texture : Texture;

        if(!this.renderer.extensions.get('OES_texture_float_linear')) {
            texture = this.createMobileTexture();
        } else {
            texture = this.createDesktopTexture();
        }

        this.textureCache.set(`${this.siteData.currentFurnishing}-${markerId}-${this.settings.isMobile}`, texture);

        return texture;
    }

    private logPixel(pixelType:string, pixelIndex:number): void {
        console.log(pixelType, pixelIndex, this.pixelData[pixelIndex], this.pixelData[pixelIndex+1], this.pixelData[pixelIndex+2], this.pixelData[pixelIndex+3]);
    }

}
